import type {
  IAdViewPremium,
  ICarLeasingAdView,
  ICarLeasingBusinessAdView,
  ICarNewAdView,
  ICarUsedAdView,
  ICarUsedBusinessAdView,
} from "@finn-no/adview-provider-frontend-types";
import type { MobilityAd } from "@mobility-item-page/types";
import { isCarAd, isCaravanAd, isMobileHomeAd, isMotorbikeAd } from "@mobility-item-page/utilities";
import type { ProGatewayFeatures } from "src/server/services/MobilityProGateway.ts";
import type { PersonalFinanceContent } from "src/types/personalFinance.ts";
import { BenefitTabs, MemberAssociationTabs, type SafetyTabs } from "../types/safetyElements.ts";

export const hasDeclartionOfGoods = (
  ad: MobilityAd,
): ad is ICarUsedAdView | ICarUsedBusinessAdView | ICarLeasingAdView | ICarLeasingBusinessAdView | ICarNewAdView => {
  return "declaration_of_goods" in ad;
};

export const hasExchangePolicyInfo = (
  ad: MobilityAd,
): ad is ICarUsedAdView | ICarUsedBusinessAdView | ICarLeasingAdView | ICarLeasingBusinessAdView | ICarNewAdView => {
  return !!("right_to_exchange_info" in ad && ad.right_to_exchange_info);
};

export const hasHomeDelivery = (
  ad: MobilityAd,
): ad is ICarUsedAdView | ICarUsedBusinessAdView | ICarLeasingAdView | ICarLeasingBusinessAdView | ICarNewAdView => {
  return !!("home_delivery" in ad && ad.home_delivery);
};

export const hasServiceHistoryInfo = (
  ad: MobilityAd,
): ad is ICarUsedAdView | ICarUsedBusinessAdView | ICarLeasingAdView | ICarLeasingBusinessAdView => {
  return !!("service_history_info" in ad && ad.service_history_info && ad.service_history_info.description);
};

export const getMobilitySafetyElementTabs = (
  ad: MobilityAd | undefined,
  associationMemberships: string[],
  usedCarGuaranteeSafetyElement: PersonalFinanceContent | null,
  proGatewayFeatures: ProGatewayFeatures,
  proGatewayEnabled: boolean,
): SafetyTabs[] => {
  if (!ad) {
    return [];
  }

  const memberOf = {
    NBF: associationMemberships.includes("NBF_CAR"),
    MobilityPool: associationMemberships.includes("MOBILITY_POOL_CAR"),
    Bruktbilgruppen: associationMemberships.includes("BRUKTBILGRUPPEN_CAR"),
    Norboat: associationMemberships.includes("NORBOAT_BOAT"),
    NCBMobileHome: associationMemberships.includes("NCB_MOBILE_HOME"),
    NCBCaravan: associationMemberships.includes("NCB_CARAVAN"),
    NMCF: associationMemberships.includes("NMCF_MC"),
  };

  // Show additional safety elements if pro gateway is disabled (e.g. not enabled for the brand) or if the feature is enabled
  const hasAdditionalSafetyElements =
    !proGatewayEnabled || (proGatewayEnabled && proGatewayFeatures.AdditionalSafetyElements);

  const tabs: SafetyTabs[] = [];
  if (usedCarGuaranteeSafetyElement) {
    tabs.push(BenefitTabs.UsedCarWarrantyPurchased);
  } else if ("warranty" in ad && ad.warranty) {
    tabs.push(BenefitTabs.Warranty);
  }
  if (hasDeclartionOfGoods(ad)) {
    tabs.push(BenefitTabs.DeclarationOfGoods);
  }

  if (isCarAd(ad) && "condition_report" in ad && ad.condition_report?.link) {
    tabs.push(BenefitTabs.Condition);
  }
  if ("service_plan_followed" in ad && ad.service_plan_followed) {
    tabs.push(BenefitTabs.ServicePlanFollowed);
  }
  if (hasServiceHistoryInfo(ad)) {
    tabs.push(BenefitTabs.ServiceHistory);
  }
  if (hasExchangePolicyInfo(ad) && hasAdditionalSafetyElements) {
    tabs.push(BenefitTabs.ExchangePolicy);
  }
  if (hasAdditionalSafetyElements && "premium" in ad && (ad.premium as IAdViewPremium).url) {
    tabs.push(BenefitTabs.Premium);
  }
  if (hasHomeDelivery(ad)) {
    tabs.push(BenefitTabs.HomeDelivery);
  }

  if (memberOf.Norboat) {
    tabs.push(MemberAssociationTabs.TradeOrg);
  }
  if (isCarAd(ad)) {
    if (memberOf.NBF) {
      tabs.push(MemberAssociationTabs.NbfMember);
    } else if (memberOf.MobilityPool) {
      tabs.push(MemberAssociationTabs.MobilityPoolMember);
    } else if (memberOf.Bruktbilgruppen) {
      tabs.push(MemberAssociationTabs.BruktbilgruppenMember);
    }
  }
  if (isMobileHomeAd(ad)) {
    if (memberOf.NCBMobileHome) {
      tabs.push(MemberAssociationTabs.NcbMember);
    }
  }
  if (isCaravanAd(ad)) {
    if (memberOf.NCBCaravan) {
      tabs.push(MemberAssociationTabs.NcbMember);
    }
  }
  if (isMotorbikeAd(ad)) {
    if (memberOf.NMCF) {
      tabs.push(MemberAssociationTabs.NmcfMember);
    }
  }

  return tabs;
};
